import { Box, BoxProps } from "@chakra-ui/react"
import { parseRgba } from "@pathwright/utils/src/color/color-utils"
import { ReactNode } from "react"
import DotBackground from "./DotBackground"

export const overlayStyles = {
  pos: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  minHeight: "100%",
  minWidth: "100%",
  overflow: "hidden"
} as const

export type BackgroundImageProps = {
  image?: string
  overlay?: string | number[] | { r: number; g: number; b: number; a?: number }
  dots?: boolean
  zoom?: boolean
  size?: string
  position?: string
  altText?: string
  children?: ReactNode
  onClick?: () => void
  className?: string
} & Pick<
  BoxProps,
  "rounded" | "roundedTop" | "roundedBottom" | "shadow" | "style"
>

const BackgroundImage = ({
  image,
  overlay,
  dots = false,
  zoom = false,
  size = "cover",
  position = "center",
  altText,
  children,
  onClick,
  rounded,
  roundedTop,
  roundedBottom,
  shadow,
  style,
  ...boxProps
}: BackgroundImageProps) => {
  const overlayColor = overlay ? parseRgba(overlay) : undefined

  const backgroundElement = (
    <Box
      pos="relative"
      className="BackgroundImage"
      borderRadius="inherit"
      onClick={onClick}
      cursor={onClick ? "pointer" : undefined}
      rounded={rounded}
      roundedTop={roundedTop}
      roundedBottom={roundedBottom}
      shadow={shadow}
      style={
        !rounded && !roundedTop && !roundedBottom
          ? {
              ...style,
              borderRadius: "inherit"
            }
          : style
      }
      {...boxProps}
    >
      {/* Background Image */}
      {image && (
        <Box
          key={image}
          {...overlayStyles}
          borderRadius="inherit"
          backgroundImage={
            image.startsWith("http") || image.startsWith("file:///")
              ? `url(${image})`
              : image // allowing for other background images, like linear-gradients
          }
          backgroundRepeat="no-repeat"
          backgroundPosition={position}
          backgroundSize={size}
          aria-label={altText}
          tabIndex={altText ? 0 : undefined}
        />
      )}

      {/* Overlay */}
      {overlayColor && (
        <Box
          key={overlayColor}
          {...overlayStyles}
          borderRadius="inherit"
          backgroundColor={overlayColor}
        />
      )}

      {/* Dots Pattern */}
      {dots && <DotBackground dotSize={4} dotSpacing={50} />}

      {/* Children */}
      {children}
    </Box>
  )

  // Wrap in zoom container if zoom animation is enabled
  if (zoom) {
    return (
      <Box {...overlayStyles}>
        <Box
          {...overlayStyles}
          animation={zoom ? "zoom 2s forwards" : undefined}
          css={{
            "@keyframes zoom": {
              "0%": {
                transform: "scale(1.01)"
              },
              "100%": {
                transform: "scale(1.04)"
              }
            }
          }}
        >
          {backgroundElement}
        </Box>
      </Box>
    )
  }

  return backgroundElement
}

BackgroundImage.displayName = "BackgroundImage"

export default BackgroundImage
